/* <shipping-bar>: fills the free-shipping progress bar from the live cart.

   Reads /cart.js on load and whenever the cart changes, then swaps between the
   "away from free shipping" and "unlocked" messages. The server-rendered markup
   already carries a sensible static message, so the bar degrades to that when
   JavaScript is unavailable or the fetch fails. */
class ShippingBar extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.threshold || 0);
    this.fill = this.querySelector('[data-bar-fill]');
    this.track = this.querySelector('[data-bar-track]');
    this.headline = this.querySelector('[data-bar-headline]');
    this.detail = this.querySelector('[data-bar-detail]');

    if (this.threshold > 0) this.refresh();

    this.onCartChange = () => this.refresh();
    document.addEventListener('cart:added', this.onCartChange);
    document.addEventListener('cart:updated', this.onCartChange);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:added', this.onCartChange);
    document.removeEventListener('cart:updated', this.onCartChange);
  }

  async refresh() {
    let cart;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) return;
      cart = await response.json();
    } catch {
      return; /* Keep the server-rendered message. */
    }
    this.render(cart.total_price || 0);
  }

  render(total) {
    const remaining = Math.max(this.threshold - total, 0);
    const ratio = this.threshold > 0 ? Math.min(total / this.threshold, 1) : 0;
    const unlocked = remaining === 0;

    if (this.fill) this.fill.style.setProperty('--oj-bar-progress', `${(ratio * 100).toFixed(1)}%`);
    if (this.track) {
      this.track.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
      this.track.setAttribute('aria-valuetext', this.money(total));
    }

    const template = unlocked ? this.dataset.messageUnlocked : this.dataset.messageAway;
    if (this.headline && template) this.headline.textContent = this.fill_template(template, remaining, total);

    const detail = unlocked ? this.dataset.detailUnlocked : this.dataset.detailAway;
    if (this.detail && detail) this.detail.textContent = this.fill_template(detail, remaining, total);

    this.classList.toggle('is-unlocked', unlocked);
  }

  fill_template(template, remaining, total) {
    return template
      .replaceAll('[[remaining]]', this.money(remaining))
      .replaceAll('[[total]]', this.money(total))
      .replaceAll('[[threshold]]', this.money(this.threshold));
  }

  /* Formats cents with the shop's money format, falling back to a plain amount. */
  money(cents) {
    const format = this.dataset.moneyFormat || '{{ amount }}';
    const amount = (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
      minimumFractionDigits: format.includes('no_decimals') ? 0 : 2,
      maximumFractionDigits: format.includes('no_decimals') ? 0 : 2,
    });
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }
}
customElements.define('shipping-bar', ShippingBar);
